// ISC License
// Copyright (c) for portions of Lucide are held by Cole Bemis 2013-2023 as part of Feather (MIT). All other copyright (c) for Lucide are held by Lucide Contributors 2025.
// Permission to use, copy, modify, and/or distribute this software for any purpose with or without fee is hereby granted, provided that the above copyright notice and this permission notice appear in all copies.
// THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.

import { useState, useEffect, useCallback } from 'react';
import { Menu, X } from 'lucide-react';
import CustomScrollLink from './CustomScrollLink';
import navItems from './../../config/constants';
import logo from 'url:./../logo_header.png';

const Header = ({ onOpenPrivacy, onOpenTerms }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [activeSection, setActiveSection] = useState('home');

    useEffect(() => {
        const sections = navItems.map(item => document.getElementById(item.href)).filter(e => e);
        if (sections.length === 0) return;

        const observerOptions = {
            root: null,
            rootMargin: '0px',
            threshold: 0.8,
        };

        const observerCallback = (entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    setActiveSection(entry.target.id);
                }
            });
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);
        sections.forEach(section => observer.observe(section));

        return () => {
            sections.forEach(section => observer.unobserve(section));
        };
    }, []);

    const handleScroll = useCallback(() => {
        setIsScrolled(window.scrollY > 50);
    }, []);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    return (
        <header className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-20">
                    <div className="flex-shrink-0">
                        <img 
                            src={logo} 
                            alt="Logo Kancelarii" 
                            className="max-h-16 w-auto transition-all duration-300"
                        />
                    </div>

                    <nav className="hidden md:flex items-center space-x-2 lg:space-x-4">
                        {navItems.map((item) => (
                            <CustomScrollLink
                                key={item.name}
                                toId={item.href}
                                isActive={activeSection === item.href}
                            >
                                {item.name}
                            </CustomScrollLink>
                        ))}
                        
                        <div className="h-6 w-[1px] bg-gray-300 mx-2"></div>
                        
                        <button 
                            onClick={onOpenPrivacy}
                            className="text-sm font-medium text-gray-600 hover:text-[#1B5125] transition-colors duration-200"
                        >
                            Prywatność
                        </button>
                    </nav>

                    <div className="md:hidden flex items-center">
                        <button
                            type="button"
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="text-[#1B5125] hover:text-green-800 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500"
                            aria-expanded={isMenuOpen}
                            aria-label="Toggle navigation"
                        >
                            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                        </button>
                    </div>
                </div>
            </div>

            <div className={`md:hidden transition-all duration-300 overflow-hidden ${isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white shadow-md border-t border-gray-100">
                    {navItems.map((item) => (
                        <CustomScrollLink
                            key={item.name}
                            toId={item.href}
                            isActive={activeSection === item.href}
                            onClick={() => setIsMenuOpen(false)}
                            className="block px-3 py-2 text-base font-medium"
                        >
                            {item.name}
                        </CustomScrollLink>
                    ))}
                    
                    <div className="pt-2 mt-2 border-t border-gray-100">
                        <button 
                            onClick={() => { onOpenPrivacy(); setIsMenuOpen(false); }}
                            className="block w-full text-left px-3 py-2 text-base font-medium text-gray-500 hover:text-[#1B5125]"
                        >
                            Polityka prywatności
                        </button>
                        <button 
                            onClick={() => { onOpenTerms(); setIsMenuOpen(false); }}
                            className="block w-full text-left px-3 py-2 text-base font-medium text-gray-500 hover:text-[#1B5125]"
                        >
                            Regulamin
                        </button>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;